import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { store } from "../store";
import {
  Box,
  TextField,
  Card,
  CardHeader,
  CardContent,
  makeStyles,
  CardActions,
} from "@material-ui/core";
import { NextButton } from "../components/NextButton";
import { useNavigate } from "react-router-dom";
import Model from "../components/Model";
import { Loading } from "../components/Loading";
import { Text } from "../components/Text";
import { useIdentityContext } from "react-netlify-identity";
import { toFixedInches } from "../utils/toFixedInches";
import { formatDateString } from "../utils/strings";
import { hornType } from "../components/InputParameters";

const fields = [
  { key: "driverName", title: "Driver Name" },
  { key: "horizontalBeamWidth", title: "Horizontal Beam Width" },
  { key: "horizontalMaxSize", title: "Horizontal Max Size" },
  { key: "upperFrequency", title: "Upper Frequency" },
  { key: "verticalBeamWidth", title: "Vertical Beam Width" },
  { key: "verticalMaxSize", title: "Vertical Max Size" },
];
export const MyDesigns = observer(() => {
  const navigate = useNavigate();
  const classes = styles();
  const [error, setError] = useState(false);
  const {
    myDesigns,
    setDesign,
    isImperial,
    fetchDesigns,
    setLoading,
    calculateHornDesign,
    resetDesigns,
  } = store;

  const { user } = useIdentityContext();

  useEffect(() => {
    setError(false);
    processFetchDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFetchDesign = async () => {
    try {
      fetchDesigns(user);
    } catch (error) {
      console.log(error);
      setError(error?.errorMessage || error?.message || error.toString());
    }
  };

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height={1}
      >
        <Text>
          An error occurred while fetching your designs. If this persists please
          try logout and back in
        </Text>
        <Text color="secondary">{error}</Text>
      </Box>
    );
  }

  if (!myDesigns) {
    return <Loading text="Finding your designs" />;
  }
  return (
    <Box className={classes.container}>
      {myDesigns?.length ? (
        <Box maxWidth={1000} width={1}>
          {myDesigns.map((design) => {
            const model =
              typeof design.result === "string"
                ? JSON.parse(design.result)
                : design.result;

            return (
              <Card key={design.created} className={classes.card}>
                <CardHeader subheader={formatDateString(design.created)} />
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box
                    minWidth={200}
                    display="flex"
                    flex={1}
                    justifyContent="center"
                    ml={1}
                  >
                    <Model
                      small
                      output={model}
                      geometry={design.parameters.geometry}
                    />
                  </Box>
                  <Box display="flex" flex={3}>
                    <CardContent style={{ display: "flex", flexWrap: "wrap" }}>
                      <TextField
                        className={classes.field}
                        label="Horn type"
                        value={
                          design.parameters.allowDiffractionSlot
                            ? hornType[1].label
                            : design.parameters.prioritizeLoading
                            ? hornType[2].label
                            : hornType[0].label
                        }
                        inputProps={{ readOnly: true, shrink: "true" }}
                      />
                      {fields.map(({ key, title }) => {
                        const isSize = title.includes("Max Size");
                        const units = isSize
                          ? isImperial
                            ? "(in)"
                            : "(mm)"
                          : "";
                        return (
                          <TextField
                            className={classes.field}
                            key={key}
                            label={title + units}
                            value={
                              isSize && isImperial
                                ? toFixedInches(design.parameters[key])
                                : design.parameters[key]
                            }
                            inputProps={{ readOnly: true, shrink: "true" }}
                          />
                        );
                      })}
                    </CardContent>
                  </Box>
                </Box>
                <CardActions className={classes.actions}>
                  <NextButton
                    text="View"
                    onClick={async () => {
                      setLoading(true);
                      resetDesigns();
                      setDesign(design);
                      navigate("/results");
                      await calculateHornDesign();
                      setLoading(false);
                    }}
                  />
                </CardActions>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Card className={classes.card}>
          <CardHeader
            title="No designs"
            subheader="When you create designs they will appear here"
          />
        </Card>
      )}
    </Box>
  );
});

const styles = makeStyles(() => ({
  container: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
  },
  card: {
    margin: "1em 0",
  },
  field: {
    margin: "0.5em 1em",
    minWidth: 200,
    dispaly: "flex",
    flexGrow: 1,
  },
  actions: {
    justifyContent: "flex-end",
  },
}));
